import clsx from 'clsx';
import { debounce } from 'lodash-es';
import throttle from 'lodash-es/throttle';
import {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { observeResize } from '../../utils';
import { Scrollable } from '../scrollbar';
import { MasonryStickyHeader } from './sticky-header';
import * as styles from './styles.css';
import type {
  MasonryGroup,
  MasonryItem,
  MasonryItemXYWH,
  MasonryPX,
} from './type';
import {
  calcActive,
  calcColumns,
  calcLayout,
  calcPX,
  calcSticky,
} from './utils';

export interface MasonryProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MasonryItem[] | MasonryGroup[];

  gapX?: number;
  gapY?: number;
  paddingX?: MasonryPX;
  paddingY?: number;

  groupsGap?: number;
  groupHeaderGapWithItems?: number;
  stickyGroupHeader?: boolean;
  collapsedGroups?: string[];
  onGroupCollapse?: (groupId: string, collapsed: boolean) => void;
  animateLayout?: boolean;
  /**
   * Specify the width of the item.
   * - `number`: The width of the item in pixels.
   * - `'stretch'`: The item will stretch to fill the container.
   * @default 'stretch'
   */
  itemWidth?: number | 'stretch';
  /**
   * The minimum width of the item in pixels.
   * @default 100
   */
  itemWidthMin?: number;
  virtualScroll?: boolean;
  locateMode?: 'transform' | 'leftTop' | 'transform3d';
  /**
   * Specify the number of columns, will override the calculated
   */
  columns?: number;
  resizeDebounce?: number;
  /** Commit resize geometry before paint, including virtualized items. */
  resizeSynchronously?: boolean;
  preloadHeight?: number;

  onStickyGroupChange?: (groupId?: string) => void;
}

export type MasonryRef = {
  scrollToGroup: (groupId: string) => void;
};

const MASONRY_GROUP_COLLAPSE_ANIMATION_MS = 180;

export const Masonry = forwardRef<MasonryRef, MasonryProps>(function Masonry(
  {
    items,
    gapX = 12,
    gapY = 12,
    itemWidth = 'stretch',
    itemWidthMin = 100,
    paddingX = 0,
    paddingY = 0,
    className,
    virtualScroll = false,
    locateMode = 'leftTop',
    groupsGap = 0,
    groupHeaderGapWithItems = 0,
    stickyGroupHeader = true,
    collapsedGroups,
    columns,
    preloadHeight = 50,
    resizeDebounce = 20,
    resizeSynchronously = false,
    onGroupCollapse,
    onStickyGroupChange,
    animateLayout = false,
    ...props
  },
  ref
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const activeFrameRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    return () => cancelAnimationFrame(activeFrameRef.current ?? 0);
  }, []);
  const [height, setHeight] = useState(0);
  const [layoutMap, setLayoutMap] = useState<
    Map<MasonryItem['id'], MasonryItemXYWH>
  >(new Map());
  /**
   * Record active items, to ensure all items won't be rendered when initialized.
   */
  const [activeMap, setActiveMap] = useState<Map<MasonryItem['id'], boolean>>(
    new Map()
  );
  const [stickyGroupId, setStickyGroupId] = useState<string | undefined>(
    undefined
  );
  const [totalWidth, setTotalWidth] = useState(0);
  const collapsedGroupIds = collapsedGroups ?? [];
  const collapsedGroupSignature = collapsedGroupIds.join('\u001f');
  const previousCollapsedGroupIdsRef = useRef<string[]>(collapsedGroupIds);
  const collapseTimersRef = useRef(new Map<string, number>());
  const [collapsingGroupIds, setCollapsingGroupIds] = useState(
    () => new Set<string>()
  );
  const stickyGroupCollapsed = !!(
    stickyGroupId && collapsedGroupIds.includes(stickyGroupId)
  );

  const groups = useMemo(() => {
    if (items.length === 0) {
      return [];
    }
    if (items[0] && 'items' in items[0]) return items as MasonryGroup[];
    return [{ id: '', height: 0, items: items as MasonryItem[] }];
  }, [items]);

  const stickyGroup = useMemo(() => {
    if (!stickyGroupId) return undefined;
    return groups.find(group => group.id === stickyGroupId);
  }, [groups, stickyGroupId]);

  const pendingCollapsingGroupIds = useMemo(() => {
    if (!animateLayout) {
      return new Set<string>();
    }
    const previous = new Set(previousCollapsedGroupIdsRef.current);
    return new Set(collapsedGroupIds.filter(id => !previous.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateLayout, collapsedGroupSignature]);

  const visibleCollapsingGroupIds = useMemo(() => {
    if (!animateLayout || pendingCollapsingGroupIds.size === 0) {
      return collapsingGroupIds;
    }
    const next = new Set(collapsingGroupIds);
    for (const id of pendingCollapsingGroupIds) {
      next.add(id);
    }
    return next;
  }, [animateLayout, collapsingGroupIds, pendingCollapsingGroupIds]);

  useEffect(() => {
    const collapseTimers = collapseTimersRef.current;
    return () => {
      for (const timeout of collapseTimers.values()) {
        window.clearTimeout(timeout);
      }
      collapseTimers.clear();
    };
  }, []);

  useEffect(() => {
    if (!animateLayout) {
      previousCollapsedGroupIdsRef.current = collapsedGroupIds;
      setCollapsingGroupIds(previous => {
        if (previous.size === 0) {
          return previous;
        }
        return new Set();
      });
      for (const timeout of collapseTimersRef.current.values()) {
        window.clearTimeout(timeout);
      }
      collapseTimersRef.current.clear();
      return;
    }

    const previous = new Set(previousCollapsedGroupIdsRef.current);
    const next = new Set(collapsedGroupIds);
    const newlyCollapsed = collapsedGroupIds.filter(id => !previous.has(id));
    const newlyExpanded = previousCollapsedGroupIdsRef.current.filter(
      id => !next.has(id)
    );

    previousCollapsedGroupIdsRef.current = collapsedGroupIds;

    if (newlyCollapsed.length > 0) {
      setCollapsingGroupIds(previousCollapsing => {
        const nextCollapsing = new Set(previousCollapsing);
        for (const id of newlyCollapsed) {
          nextCollapsing.add(id);
        }
        return nextCollapsing;
      });

      for (const id of newlyCollapsed) {
        const previousTimer = collapseTimersRef.current.get(id);
        if (previousTimer) {
          window.clearTimeout(previousTimer);
        }
        collapseTimersRef.current.set(
          id,
          window.setTimeout(() => {
            collapseTimersRef.current.delete(id);
            setCollapsingGroupIds(previousCollapsing => {
              if (!previousCollapsing.has(id)) {
                return previousCollapsing;
              }
              const nextCollapsing = new Set(previousCollapsing);
              nextCollapsing.delete(id);
              return nextCollapsing;
            });
          }, MASONRY_GROUP_COLLAPSE_ANIMATION_MS)
        );
      }
    }

    if (newlyExpanded.length > 0) {
      for (const id of newlyExpanded) {
        const timeout = collapseTimersRef.current.get(id);
        if (timeout) {
          window.clearTimeout(timeout);
          collapseTimersRef.current.delete(id);
        }
      }
      setCollapsingGroupIds(previousCollapsing => {
        let changed = false;
        const nextCollapsing = new Set(previousCollapsing);
        for (const id of newlyExpanded) {
          changed = nextCollapsing.delete(id) || changed;
        }
        return changed ? nextCollapsing : previousCollapsing;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateLayout, collapsedGroupSignature]);

  const layoutCollapsedGroupIds = useMemo(() => {
    if (!animateLayout || visibleCollapsingGroupIds.size === 0) {
      return collapsedGroupIds;
    }
    return collapsedGroupIds.filter(id => !visibleCollapsingGroupIds.has(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateLayout, collapsedGroupSignature, visibleCollapsingGroupIds]);

  const updateActiveMap = useCallback(
    (
      layoutMap: Map<MasonryItem['id'], MasonryItemXYWH>,
      _scrollY?: number,
      immediate = false
    ) => {
      if (!virtualScroll) return;

      const rootEl = rootRef.current;
      if (!rootEl) return;

      cancelAnimationFrame(activeFrameRef.current ?? 0);
      activeFrameRef.current = undefined;
      const update = () => {
        activeFrameRef.current = undefined;
        const scrollY = _scrollY ?? rootEl.scrollTop;
        const activeMap = calcActive({
          viewportHeight: rootEl.clientHeight,
          scrollY,
          layoutMap,
          preloadHeight,
        });
        setActiveMap(activeMap);
      };
      if (immediate) update();
      else activeFrameRef.current = requestAnimationFrame(update);
    },
    [preloadHeight, virtualScroll]
  );

  const calculateLayout = useCallback(
    (immediate = false) => {
      const rootEl = rootRef.current;
      if (!rootEl) return;

      const totalWidth = rootEl.clientWidth;
      const { columns: calculatedColumns, width } = calcColumns(
        totalWidth,
        itemWidth,
        itemWidthMin,
        gapX,
        paddingX,
        columns
      );

      const { layout, height } = calcLayout(groups, {
        totalWidth,
        columns: calculatedColumns,
        width,
        gapX,
        gapY,
        paddingX,
        paddingY,
        groupsGap,
        groupHeaderGapWithItems,
        collapsedGroups: layoutCollapsedGroupIds,
      });
      setLayoutMap(layout);
      setHeight(height);
      setTotalWidth(totalWidth);
      updateActiveMap(layout, undefined, immediate);
      if (stickyGroupHeader && rootRef.current) {
        setStickyGroupId(
          calcSticky({ scrollY: rootRef.current.scrollTop, layoutMap: layout })
        );
      }
    },
    [
      columns,
      gapX,
      gapY,
      groupHeaderGapWithItems,
      groups,
      groupsGap,
      itemWidth,
      itemWidthMin,
      paddingX,
      paddingY,
      layoutCollapsedGroupIds,
      stickyGroupHeader,
      updateActiveMap,
    ]
  );

  // handle resize
  useLayoutEffect(() => {
    calculateLayout(true);
    if (rootRef.current) {
      if (resizeSynchronously) {
        // Sidebar grid geometry changes during layout. A deferred React commit
        // or another animation frame would leave item widths one paint behind.
        return observeResize(rootRef.current, () => {
          flushSync(() => calculateLayout(true));
        });
      }
      if (resizeDebounce <= 0) {
        return observeResize(rootRef.current, () => calculateLayout());
      }
      return observeResize(
        rootRef.current,
        debounce(() => calculateLayout(), resizeDebounce)
      );
    }
    return;
  }, [calculateLayout, resizeDebounce, resizeSynchronously]);

  // handle scroll
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    if (virtualScroll || stickyGroupHeader) {
      const updateVirtualItems = throttle((scrollY: number) => {
        updateActiveMap(layoutMap, scrollY);
      }, 50);
      const handler = (e: Event) => {
        const scrollY = (e.target as HTMLElement).scrollTop;
        if (virtualScroll) updateVirtualItems(scrollY);
        if (stickyGroupHeader) {
          const stickyGroupId = calcSticky({ scrollY, layoutMap });
          setStickyGroupId(stickyGroupId);
          onStickyGroupChange?.(stickyGroupId);
        }
      };
      rootEl.addEventListener('scroll', handler);
      return () => {
        rootEl.removeEventListener('scroll', handler);
        updateVirtualItems.cancel();
      };
    }
    return;
  }, [
    layoutMap,
    onStickyGroupChange,
    stickyGroupHeader,
    updateActiveMap,
    virtualScroll,
  ]);

  const scrollToGroup = useCallback(
    (groupId: string) => {
      const group = layoutMap.get(groupId);
      if (!group) return;
      rootRef.current?.scrollTo({
        top: group.y,
        behavior: 'instant',
      });
    },
    [layoutMap]
  );

  useImperativeHandle<MasonryRef, MasonryRef>(ref, () => {
    return { scrollToGroup };
  });

  return (
    <Scrollable.Root className={styles.container}>
      <Scrollable.Viewport
        ref={rootRef}
        data-masonry-root
        className={clsx('scrollable', styles.root, className)}
        {...props}
      >
        {groups.map(group => {
          // sleep is not calculated, do not render
          const {
            id: groupId,
            items,
            className,
            Component,
            ...groupProps
          } = group;
          const collapsed = collapsedGroupIds.includes(groupId);
          const isCollapsing =
            collapsed &&
            animateLayout &&
            visibleCollapsingGroupIds.has(groupId);
          const shouldRenderItems = !collapsed || isCollapsing;

          return (
            <Fragment key={groupId}>
              {/* group header */}
              {virtualScroll && !activeMap.get(group.id) ? null : (
                <MasonryGroupHeader
                  className={clsx(styles.groupHeader, className)}
                  key={`header-${groupId}`}
                  id={groupId}
                  locateMode={locateMode}
                  xywh={layoutMap.get(groupId)}
                  {...groupProps}
                  onClick={() => onGroupCollapse?.(groupId, !collapsed)}
                  Component={Component}
                  itemCount={items.length}
                  collapsed={!!collapsed}
                  groupId={groupId}
                  paddingX={calcPX(paddingX, totalWidth)}
                  animateLayout={animateLayout}
                />
              )}
              {/* group items */}
              {shouldRenderItems
                ? items.map(({ id: itemId, Component, ...item }) => {
                    const mixId = groupId ? `${groupId}:${itemId}` : itemId;
                    if (virtualScroll && !activeMap.get(mixId)) return null;
                    return (
                      <MasonryGroupItem
                        key={mixId}
                        id={mixId}
                        {...item}
                        locateMode={locateMode}
                        xywh={layoutMap.get(mixId)}
                        groupId={groupId}
                        itemId={itemId}
                        Component={Component}
                        animateLayout={animateLayout}
                        presence={isCollapsing ? 'exiting' : 'present'}
                      />
                    );
                  })
                : null}
            </Fragment>
          );
        })}
        <div
          data-masonry-placeholder
          className={animateLayout ? styles.placeholderAnimated : undefined}
          style={{ height }}
        />
      </Scrollable.Viewport>
      {stickyGroup ? (
        <MasonryStickyHeader
          groupId={stickyGroup.id}
          className={clsx(styles.stickyGroupHeader, stickyGroup.className)}
          style={{
            padding: `0 ${calcPX(paddingX, totalWidth)}px`,
            height: stickyGroup.height,
            ...stickyGroup.style,
          }}
          onClick={() =>
            onGroupCollapse?.(stickyGroup.id, !stickyGroupCollapsed)
          }
        >
          {stickyGroup.Component ? (
            <stickyGroup.Component
              groupId={stickyGroup.id}
              itemCount={stickyGroup.items.length}
              collapsed={stickyGroupCollapsed}
            />
          ) : (
            stickyGroup.children
          )}
        </MasonryStickyHeader>
      ) : null}
      <Scrollable.Scrollbar className={styles.scrollbar} />
    </Scrollable.Root>
  );
});

type MasonryItemProps = MasonryItem &
  Omit<React.HTMLAttributes<HTMLDivElement>, 'id' | 'height'> & {
    animateLayout?: boolean;
    locateMode?: 'transform' | 'leftTop' | 'transform3d';
    presence?: 'present' | 'exiting';
    xywh?: MasonryItemXYWH;
  };

const MasonryGroupHeader = memo(function MasonryGroupHeader({
  id,
  children,
  style,
  className,
  animateLayout,
  Component,
  groupId,
  itemCount,
  collapsed,
  paddingX,
  ...props
}: Omit<MasonryItemProps, 'Component'> & {
  Component?: MasonryGroup['Component'];
  groupId: string;
  itemCount: number;
  collapsed: boolean;
  paddingX?: number;
}) {
  const content = useMemo(() => {
    if (Component) {
      return (
        <Component
          groupId={groupId}
          itemCount={itemCount}
          collapsed={collapsed}
        />
      );
    }
    return children;
  }, [Component, children, collapsed, groupId, itemCount]);

  return (
    <MasonryItem
      id={id}
      style={{
        padding: `0 ${paddingX}px`,
        height: '100%',
        ...style,
      }}
      className={className}
      animateLayout={animateLayout}
      {...props}
    >
      {content}
    </MasonryItem>
  );
});

const MasonryGroupItem = memo(function MasonryGroupItem({
  id,
  children,
  className,
  Component,
  groupId,
  itemId,
  animateLayout,
  presence,
  ...props
}: MasonryItemProps & {
  groupId: string;
  itemId: string;
}) {
  const content = useMemo(() => {
    if (Component) {
      return <Component groupId={groupId} itemId={itemId} />;
    }
    return children;
  }, [Component, children, groupId, itemId]);

  return (
    <MasonryItem
      id={id}
      className={className}
      animateLayout={animateLayout}
      presence={presence}
      {...props}
    >
      {content}
    </MasonryItem>
  );
});

const MasonryItem = memo(function MasonryItem({
  id,
  xywh,
  locateMode = 'leftTop',
  children,
  className,
  animateLayout,
  presence = 'present',
  style: styleProp,
  ...props
}: Omit<MasonryItemProps, 'height' | 'ratio'>) {
  const style = useMemo(() => {
    if (!xywh) return { display: 'none' };

    const { x, y, w, h } = xywh;

    const posStyle =
      locateMode === 'transform'
        ? { transform: `translate(${x}px, ${y}px)` }
        : locateMode === 'leftTop'
          ? { left: `${x}px`, top: `${y}px` }
          : { transform: `translate3d(${x}px, ${y}px, 0)` };

    return {
      left: 0,
      top: 0,
      ...styleProp,
      ...posStyle,
      width: `${w}px`,
      height: `${h}px`,
    };
  }, [locateMode, styleProp, xywh]);

  if (!xywh) return null;

  return (
    <div
      data-masonry-item
      data-masonry-item-id={id}
      data-masonry-presence={presence}
      style={style}
      className={clsx(
        styles.item,
        animateLayout && styles.itemAnimated,
        presence === 'exiting' && styles.itemExiting,
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
